import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { formatApiErrorDetail } from '@/lib/errors';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function AuthModal({ open, onOpenChange, defaultTab = 'login' }) {
  const { login, register } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [error, setError] = useState('');

  // Sync tab when the parent flips defaultTab while reopening the modal
  React.useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (tab === 'login') {
        await login(form.email, form.password);
        toast.success('Bienvenue !');
      } else {
        await register(form.email, form.password, form.name || form.email.split('@')[0]);
        toast.success('Compte créé.');
      }
      onOpenChange(false);
      setForm({ email: '', password: '', name: '' });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const discordLogin = async () => {
    try {
      const { data } = await api.get('/auth/discord/login');
      window.location.href = data.url;
    } catch {
      toast.error('Discord indisponible.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase tracking-tighter">
            {tab === 'login' ? t('auth.title.login') : t('auth.title.register')}
          </DialogTitle>
          <DialogDescription className="text-white/60">{t('auth.desc')}</DialogDescription>
        </DialogHeader>

        {/* Discord button - top */}
        <Button
          type="button"
          data-testid="auth-discord-btn"
          onClick={discordLogin}
          className="w-full bg-[#5865F2] hover:bg-[#4752c4] text-white font-bold uppercase tracking-wide rounded-full h-11"
        >
          <svg viewBox="0 0 71 55" className="w-5 h-5 mr-2 fill-current" aria-hidden>
            <path d="M60.1 4.9A58.5 58.5 0 0 0 46 0c-.7 1.2-1.4 2.8-1.9 4.1a54.7 54.7 0 0 0-16.3 0C27.3 2.8 26.6 1.2 25.9 0 21 .8 16.4 2.4 12 4.9 2.7 18.9.2 32.4 1.4 45.8a59.3 59.3 0 0 0 18.1 9.1c1.5-2 2.8-4.2 3.9-6.5a37.6 37.6 0 0 1-6.2-3l1.5-1.2a42 42 0 0 0 36.6 0l1.5 1.2c-2 1.1-4 2.1-6.2 3 1.1 2.3 2.4 4.5 3.9 6.5a59 59 0 0 0 18.1-9.1c1.4-15.6-2.7-29-9.5-40.9zM23.7 37.7c-3.6 0-6.5-3.3-6.5-7.3 0-4 2.9-7.3 6.5-7.3s6.6 3.3 6.5 7.3c0 4-2.9 7.3-6.5 7.3zm24 0c-3.6 0-6.5-3.3-6.5-7.3 0-4 2.9-7.3 6.5-7.3s6.6 3.3 6.5 7.3c0 4-2.9 7.3-6.5 7.3z"/>
          </svg>
          {t('auth.discord')}
        </Button>

        <div className="relative my-3">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10" />
          </div>
          <div className="relative flex justify-center text-[10px] uppercase tracking-[0.2em]">
            <span className="bg-[#0a0a0a] px-3 text-white/40">{t('auth.or')}</span>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 bg-white/5 border border-white/10">
            <TabsTrigger value="login" data-testid="auth-tab-login">{t('auth.tab.login')}</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">{t('auth.tab.register')}</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-4">
            <form onSubmit={submit} className="space-y-4">
              {tab === 'register' && (
                <div>
                  <Label htmlFor="name" className="text-white/80">{t('auth.name')}</Label>
                  <Input
                    id="name"
                    data-testid="auth-input-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="ApexHunter"
                    className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-white/80">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="auth-input-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="driver@horizon.com"
                  className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-white/80">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  data-testid="auth-input-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
                />
              </div>
              {error && (
                <div data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="w-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide rounded-full"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  tab === 'login' ? t('auth.submit.login') : t('auth.submit.register')}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
